import { type RewriteOption } from '@airship-platform/airship-patterns';
import {
  AIEditOptions,
  FlexBox,
  LoadingButton,
} from '@airship-platform/jam-web/components';
import { HTMLEditor } from '@airship-platform/jam-web/components-x';
import { LeftArrowIcon } from '@airship-platform/jam-web/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import {
  Controller,
  type ControllerRenderProps,
  FormProvider,
  useForm,
} from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';
import { type AppointmentProposalOption } from '../../data-access/appointmentProposalOptions';
import ROUTE_PATHS from '../../routes/paths';

const FormProposalOptionSummarySchema = z.object({
  summary: z.string(),
  summaryTextPlain: z
    .string()
    .max(5000, 'Summary must be at most 5000 characters')
    .refine((val) => val.length === 0 || val.length >= 20, {
      error: 'Summary must be at least 20 characters',
    }),
});

export type FormProposalOptionSummaryInputs = z.infer<
  typeof FormProposalOptionSummarySchema
>;

interface FormProposalOptionSummaryProps {
  isLoading: boolean;
  isDisabled?: boolean;
  isGenerating: RewriteOption | null;
  previousSummary: string | null;
  defaultValues: FormProposalOptionSummaryInputs;
  currentOption: AppointmentProposalOption;
  onSubmit: (data: FormProposalOptionSummaryInputs) => void;
  onAIGenerate: (
    field: ControllerRenderProps<FormProposalOptionSummaryInputs, 'summary'>,
    rewriteOption: RewriteOption
  ) => Promise<void>;
  onUndo: (
    field: ControllerRenderProps<FormProposalOptionSummaryInputs, 'summary'>
  ) => void;
}

const FormProposalOptionSummary: React.FC<FormProposalOptionSummaryProps> = ({
  isLoading,
  isDisabled,
  isGenerating,
  previousSummary,
  defaultValues,
  currentOption,
  onSubmit,
  onAIGenerate,
  onUndo,
}) => {
  const formMethods = useForm<FormProposalOptionSummaryInputs>({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(FormProposalOptionSummarySchema),
  });

  const { control, handleSubmit, formState, setValue } = formMethods;

  return (
    <FlexBox
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      maxWidth="40rem"
      gap={4}
      width="100%"
    >
      <Box
        component="form"
        autoComplete="off"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          maxWidth: '40rem',
          width: '100%',
        }}
      >
        <FormProvider {...formMethods}>
          <Controller
            name="summary"
            control={control}
            render={({ field }) => (
              <FlexBox flexDirection="column" gap={2}>
                <HTMLEditor
                  disabled={!!isGenerating || isDisabled}
                  limit={5000}
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                  onTextChange={(text) => {
                    setValue('summaryTextPlain', text, {
                      shouldValidate: true,
                    });
                  }}
                  label="Summary"
                  error={!!formState.errors.summaryTextPlain}
                  helperText={formState.errors.summaryTextPlain?.message}
                />
                <FlexBox gap={2} flexWrap="wrap">
                  <AIEditOptions
                    onClick={(rewriteOption) =>
                      onAIGenerate(field, rewriteOption)
                    }
                    disabled={!!isGenerating || isDisabled}
                    isLoadingOption={isGenerating ?? undefined}
                  />
                  <LoadingButton
                    color="softGold"
                    startIcon={<LeftArrowIcon />}
                    onClick={() => onUndo(field)}
                    disabled={!!isGenerating || !previousSummary || isDisabled}
                    loading={false}
                  >
                    Undo update
                  </LoadingButton>
                </FlexBox>
              </FlexBox>
            )}
          />
        </FormProvider>
      </Box>
      <FlexBox alignItems="center" justifyContent="space-between" width="100%">
        <Button
          variant="outlined"
          color="gray"
          component={Link}
          to={ROUTE_PATHS.PROPOSAL_OPTIONS_PATH({
            appointmentId: currentOption.appointmentId,
            appointmentProposalId: currentOption.appointmentProposalId,
            appointmentProposalOptionId:
              currentOption.appointmentProposalOptionId,
          })}
          type="button"
        >
          Cancel
        </Button>
        <LoadingButton
          loading={isLoading}
          disabled={isDisabled}
          onClick={handleSubmit(onSubmit)}
        >
          Save changes
        </LoadingButton>
      </FlexBox>
    </FlexBox>
  );
};

export default FormProposalOptionSummary;
